import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import {
  CAMPAIGN_MEMBER_SEGMENTS,
  SURVEY_QUESTION_TYPES,
  SURVEY_STATUSES,
} from "@demore/shared/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useWorkspace, useWorkspaceCan } from "./WorkspaceContext";

type Survey = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  audienceSegment: string | null;
  createdAt: string;
};

type QuestionDraft = {
  prompt: string;
  type: string;
  required: boolean;
  optionsText: string;
};

const isChoice = (t: string) => t === "single_choice" || t === "multi_choice";
const pretty = (s: string | null | undefined) => (s ?? "—").replace(/_/g, " ");

// Board #2430: standard survey templates. Picking one pre-fills the create
// form (name/description/questions) — everything stays editable, and "Custom"
// keeps the blank form. Question types come from SURVEY_QUESTION_TYPES.
const SURVEY_TEMPLATES: Record<
  string,
  { name: string; description: string; questions: QuestionDraft[] }
> = {
  client_satisfaction: {
    name: "Client Satisfaction",
    description: "How satisfied this campaign's clients are with our work.",
    questions: [
      { prompt: "How satisfied are you with our work on this campaign?", type: "rating", required: true, optionsText: "" },
      { prompt: "How likely are you to recommend us?", type: "rating", required: true, optionsText: "" },
      { prompt: "How responsive have we been?", type: "single_choice", required: false, optionsText: "Very responsive, Responsive, Slow, Unresponsive" },
      { prompt: "What could we improve?", type: "long_text", required: false, optionsText: "" },
    ],
  },
  associate_feedback: {
    name: "Associate Feedback",
    description: "How supported the campaign team feels in their day-to-day work.",
    questions: [
      { prompt: "How clear are your goals and responsibilities on this campaign?", type: "rating", required: true, optionsText: "" },
      { prompt: "Do you have the tools and support you need?", type: "boolean", required: true, optionsText: "" },
      { prompt: "How manageable is your current workload?", type: "rating", required: false, optionsText: "" },
      { prompt: "What would help you do your best work?", type: "long_text", required: false, optionsText: "" },
    ],
  },
  investor_sentiment: {
    name: "Investor Sentiment",
    description: "How confident stakeholders feel about this campaign's direction.",
    questions: [
      { prompt: "How confident are you in this campaign's direction?", type: "rating", required: true, optionsText: "" },
      { prompt: "How would you describe your outlook?", type: "single_choice", required: false, optionsText: "Very optimistic, Optimistic, Neutral, Concerned" },
      { prompt: "Is the current level of reporting sufficient?", type: "boolean", required: false, optionsText: "" },
      { prompt: "Any questions or concerns you'd like addressed?", type: "long_text", required: false, optionsText: "" },
    ],
  },
  campaign_readiness: {
    name: "Campaign Readiness",
    description: "Whether the team is prepared to represent this campaign.",
    questions: [
      { prompt: "Have you reviewed all campaign materials?", type: "boolean", required: true, optionsText: "" },
      { prompt: "How ready do you feel to represent this campaign?", type: "rating", required: true, optionsText: "" },
      { prompt: "Which areas need more preparation?", type: "multi_choice", required: false, optionsText: "Product knowledge, Pricing, Objection handling, Demo flow, None" },
      { prompt: "What's blocking you from being fully ready?", type: "long_text", required: false, optionsText: "" },
    ],
  },
  post_onboarding_review: {
    name: "Post-Onboarding Review",
    description: "A quick review after completing the onboarding pack.",
    questions: [
      { prompt: "How would you rate your onboarding experience?", type: "rating", required: true, optionsText: "" },
      { prompt: "Do you feel ready to start in your role?", type: "boolean", required: true, optionsText: "" },
      { prompt: "What could we improve about onboarding?", type: "long_text", required: false, optionsText: "" },
    ],
  },
};

export function SurveysTab() {
  const { campaignId } = useWorkspace();
  // #2504-2506: only roles that can actually manage surveys see the controls.
  const { manageSurveys } = useWorkspaceCan();
  const qc = useQueryClient();
  const base = `/api/admin/campaigns/${campaignId}/surveys`;
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Survey | null>(null);
  const [viewingResponses, setViewingResponses] = useState<Survey | null>(null);

  const { data } = useQuery({
    queryKey: ["surveys", campaignId],
    queryFn: () => api.get<{ items: Survey[] }>(base),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/${id}`),
    onSuccess: () => {
      toast.success("Survey deleted");
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ["surveys", campaignId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  // A draft survey is invisible to viewers until it's activated, and there was
  // no UI to change status after creation (an unfollowable "activate it" step).
  // This toggle wires the existing PATCH so a draft can go active (and back).
  const setStatus = useMutation({
    mutationFn: (vars: { id: string; status: string }) =>
      api.patch(`${base}/${vars.id}`, { status: vars.status }),
    onSuccess: (_d, vars) => {
      toast.success(vars.status === "active" ? "Survey activated" : "Survey paused");
      qc.invalidateQueries({ queryKey: ["surveys", campaignId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head text-lg font-semibold">Surveys</h2>
          <p className="text-xs text-muted-foreground">
            Campaign-scoped surveys — e.g. Client Satisfaction, Associate Feedback, Post-Onboarding Review.
          </p>
        </div>
        {manageSurveys && <Button onClick={() => setCreateOpen(true)}>Create Survey</Button>}
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-admin-rule">
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium">Audience</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-admin-rule">
                <td className="p-3">
                  <div className="font-medium">{s.name}</div>
                  {s.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">{s.description}</div>
                  )}
                </td>
                <td className="p-3">{pretty(s.audienceSegment)}</td>
                <td className="p-3">
                  <Badge variant={s.status === "active" ? "ok" : "muted"}>{s.status}</Badge>
                </td>
                <td className="p-3 text-right space-x-1">
                  {manageSurveys && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setStatus.mutate({ id: s.id, status: s.status === "active" ? "draft" : "active" })
                      }
                      disabled={setStatus.isPending}
                    >
                      {s.status === "active" ? "Pause" : "Activate"}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setViewingResponses(s)}>
                    Responses
                  </Button>
                  {manageSurveys && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => setPendingDelete(s)}
                      aria-label="Delete survey"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-xs text-muted-foreground">
                  No surveys yet. Create one to collect feedback for this campaign.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <CreateSurveyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        base={base}
        onSaved={() => qc.invalidateQueries({ queryKey: ["surveys", campaignId] })}
      />

      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete survey "{pendingDelete?.name}"?</DialogTitle>
            <DialogDescription>
              This permanently removes the survey and its questions and responses.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResponsesDialog
        survey={viewingResponses}
        base={base}
        onClose={() => setViewingResponses(null)}
      />
    </div>
  );
}

function ResponsesDialog({
  survey,
  base,
  onClose,
}: {
  survey: Survey | null;
  base: string;
  onClose: () => void;
}) {
  const { data } = useQuery({
    queryKey: ["survey-responses", survey?.id],
    queryFn: () =>
      api.get<{
        items: Array<{
          id: string;
          respondentRef: string | null;
          submittedAt: string;
          answers: Record<string, unknown>;
        }>;
      }>(`${base}/${survey!.id}/responses`),
    enabled: !!survey,
  });
  // Map questionId -> prompt so answers read as questions, not UUIDs.
  const { data: detail } = useQuery({
    queryKey: ["survey-detail", survey?.id],
    queryFn: () =>
      api.get<{ questions: Array<{ id: string; prompt: string }> }>(`${base}/${survey!.id}`),
    enabled: !!survey,
  });
  const promptOf = (qid: string) => detail?.questions.find((q) => q.id === qid)?.prompt ?? qid;
  const items = data?.items ?? [];

  return (
    <Dialog open={!!survey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Responses — {survey?.name}</DialogTitle>
          <DialogDescription>
            {items.length} response{items.length === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {items.length === 0 && (
            <p className="text-xs text-muted-foreground">No responses yet.</p>
          )}
          <ul className="space-y-3">
            {items.map((r) => (
              <li key={r.id} className="rounded-md border border-admin-rule p-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{r.respondentRef ?? "anonymous"}</span>
                  <span>{new Date(r.submittedAt).toLocaleString()}</span>
                </div>
                <dl className="mt-2 space-y-1 text-sm">
                  {Object.entries(r.answers ?? {}).map(([q, a]) => (
                    <div key={q} className="grid grid-cols-[1fr_2fr] gap-2">
                      <dt className="text-muted-foreground">{promptOf(q)}</dt>
                      <dd>{typeof a === "object" ? JSON.stringify(a) : String(a)}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateSurveyDialog({
  open,
  onOpenChange,
  base,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  base: string;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    status: "draft",
    audienceSegment: "",
  });
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [templateKey, setTemplateKey] = useState("");

  const reset = () => {
    setForm({ name: "", description: "", status: "draft", audienceSegment: "" });
    setQuestions([]);
    setTemplateKey("");
  };

  // Applying a template replaces name/description/questions (all editable
  // after); "Custom" returns to a blank form.
  const applyTemplate = (key: string) => {
    setTemplateKey(key);
    const tpl = SURVEY_TEMPLATES[key];
    if (!tpl) {
      setForm((f) => ({ ...f, name: "", description: "" }));
      setQuestions([]);
      return;
    }
    setForm((f) => ({ ...f, name: tpl.name, description: tpl.description }));
    setQuestions(tpl.questions.map((q) => ({ ...q })));
  };

  const save = useMutation({
    mutationFn: () =>
      api.post(base, {
        name: form.name,
        description: form.description || undefined,
        status: form.status,
        audienceSegment: form.audienceSegment || null,
        questions: questions
          .filter((q) => q.prompt.trim())
          .map((q) => ({
            prompt: q.prompt,
            type: q.type,
            required: q.required,
            options: isChoice(q.type)
              ? q.optionsText.split(",").map((s) => s.trim()).filter(Boolean)
              : undefined,
          })),
      }),
    onSuccess: () => {
      toast.success("Survey created");
      onSaved();
      reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  const addQuestion = () =>
    setQuestions((qs) => [...qs, { prompt: "", type: "short_text", required: false, optionsText: "" }]);
  const updateQuestion = (i: number, patch: Partial<QuestionDraft>) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const removeQuestion = (i: number) =>
    setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New survey</DialogTitle>
          <DialogDescription>This survey belongs only to this campaign.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Start from a template</Label>
              <Select value={templateKey} onChange={(e) => applyTemplate(e.target.value)}>
                <option value="">Custom (blank)</option>
                {Object.entries(SURVEY_TEMPLATES).map(([k, t]) => (
                  <option key={k} value={k}>{t.name}</option>
                ))}
              </Select>
              {templateKey && (
                <p className="text-xs text-muted-foreground">
                  Pre-filled from the template — edit anything below before creating.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Post-Onboarding Review"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  {SURVEY_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Audience</Label>
                <Select
                  value={form.audienceSegment}
                  onChange={(e) => setForm((f) => ({ ...f, audienceSegment: e.target.value }))}
                >
                  <option value="">— any —</option>
                  {CAMPAIGN_MEMBER_SEGMENTS.map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="pt-2 border-t border-admin-rule space-y-2">
              <div className="flex items-center justify-between">
                <Label>Questions</Label>
                <Button size="sm" variant="ghost" onClick={addQuestion}>+ Add question</Button>
              </div>
              {questions.length === 0 && (
                <p className="text-xs text-muted-foreground">No questions yet.</p>
              )}
              {questions.map((q, i) => (
                <div key={i} className="rounded-md border border-admin-rule p-2 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      className="flex-1"
                      value={q.prompt}
                      onChange={(e) => updateQuestion(i, { prompt: e.target.value })}
                      placeholder="Question prompt"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => removeQuestion(i)}
                      aria-label="Remove question"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={q.type}
                      onChange={(e) => updateQuestion(i, { type: e.target.value })}
                    >
                      {SURVEY_QUESTION_TYPES.map((t) => (
                        <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                      ))}
                    </Select>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={q.required}
                        onChange={(e) => updateQuestion(i, { required: e.target.checked })}
                      />
                      Required
                    </label>
                  </div>
                  {isChoice(q.type) && (
                    <Input
                      value={q.optionsText}
                      onChange={(e) => updateQuestion(i, { optionsText: e.target.value })}
                      placeholder="Options, comma-separated"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.name || save.isPending}>
            {save.isPending ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
